import { Zone } from "@apis/index";
import { deletesvg, edit, eye } from "@assets/icons";
import Column from "@components/column";
import Fields from "@components/fields";
import Table from "@components/table";
import { useModal } from "@context/ModalContext";
import { RootState } from "@store/index";
import theme from "@utils/theme";
import { Form, Formik } from "formik";
import { BadgeDollarSignIcon, DollarSign, Locate } from "lucide-react";
import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Truck } from "lucide-react";
import DeleteModal from "@components/delete-modal";

const ZoneList = () => {
  const { showModal } = useModal();
  const navigate = useNavigate();

  const { data: zones } = useSelector((state: RootState) => state.zones);

  const fetchZones = async () => {
    await Zone.list({ state: "zones" });
  };

  useEffect(() => {
    fetchZones();
  }, []);

  const columns: any = [
    {
      title: "ID",
      value: "id",
      render: (item) => (
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "6px 12px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            borderRadius: "8px",
            color: "#fff",
            fontWeight: "600",
            fontSize: "13px",
            minWidth: "50px",
            boxShadow: "0 2px 8px rgba(102, 126, 234, 0.25)",
          }}
        >
          #{item.id}
        </div>
      ),
    },
    {
      title: "Zone Name",
      value: "name",
      render: (item) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "36px",
              height: "36px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
              boxShadow: "0 4px 12px rgba(245, 87, 108, 0.25)",
            }}
          >
            <Locate size={18} color="#fff" />
          </div>
          <div>
            <div
              style={{
                fontWeight: "600",
                fontSize: "15px",
                color: "#1a202c",
                marginBottom: "2px",
              }}
            >
              {item.name}
            </div>
            <div
              style={{
                fontSize: "12px",
                color: "#718096",
              }}
            >
              Delivery Zone
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Modules",
      value: "module",
      render: (item: any) => (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "6px",
            maxWidth: "250px",
          }}
        >
          {item?.modules?.length > 0 ? (
            item.modules.map((m, index) => (
              <span
                key={index}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "5px 12px",
                  background:
                    index % 3 === 0
                      ? "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)"
                      : index % 3 === 1
                        ? "linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)"
                        : "linear-gradient(135deg, #d299c2 0%, #fef9d7 100%)",
                  borderRadius: "6px",
                  fontSize: "12px",
                  fontWeight: "500",
                  color: "#2d3748",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)",
                }}
              >
                {m.name}
              </span>
            ))
          ) : (
            <span
              style={{
                fontSize: "13px",
                color: "#a0aec0",
                fontStyle: "italic",
              }}
            >
              No modules
            </span>
          )}
        </div>
      ),
    },
    {
      title: "Created At",
      value: "createdAt",
      render: (item: any) => {
        const date = new Date(item.createdAt);
        const formattedDate = date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
        const formattedTime = date.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        });
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#2d3748",
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
              {formattedDate}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "13px",
                color: "#718096",
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              {formattedTime}
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <Table
      text="Zone List"
      icon={<Locate />}
      data={zones}
      columns={columns}
      actions={[
        {
          key: "delivery",
          icon: <Truck size={20} color={theme.colors.primary} />,
          onClick: (item) => {
            navigate(`/admin/zone-setup/delivery-charge/${item.id}`);
          },
        },
        {
          key: "surge",
          icon: <BadgeDollarSignIcon color={theme.colors.primary} />,
          onClick: (item) => {
            navigate(`/admin/zone-setup/surge-price/${item.id}`);
          },
        },

        {
          key: "edit",
          icon: edit,
          onClick: (item) => {
            navigate(`/admin/zone-setup/${item.id}`);
          },
        },
        {
          key: "remove",
          icon: deletesvg,
          onClick: (item: any) => {
            showModal(
              <DeleteModal
                api={Zone}
                id={item.id}
                state="zones"
                itemName={item.name}
                itemType="zone"
                onSuccess={() => fetchZones()}
              />,
            );
          },
        },
      ]}
    />
  );
};

export default ZoneList;
